import React from 'react'
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material'
import { useSelector, useDispatch } from 'react-redux'
import { ITimecard } from '../interface/ISelectedDate'
import { ENV_JID_BREAK, ENV_JID_START, ENV_JID_STOP } from '../utils/values'
import { t } from 'i18next'
import { setBaseline } from '../source/slice/BaselineSlice'

export const useUnsavedChanges = () => {
    const dispatch = useDispatch()
    const selectedDate: ITimecard = useSelector((state: any) => state.selectedDate?.value)
    const baseline: string | null = useSelector((state: any) => state.baseline?.value) ?? null
    const reduceForCompare = (tc?: ITimecard) => {
        if (!tc) return null

        const header = {
            travelTime: tc.header?.mserp_mbquantitytravel ?? 0,
        }

        const start = tc.lines?.find((l: any) => l.mserp_timeandattendancejobid === ENV_JID_START && !l.delete)
        const stop = tc.lines?.find((l: any) => l.mserp_timeandattendancejobid === ENV_JID_STOP && !l.delete)

        const toMs = (v: any) => {
            if (!v) return null
            const t = new Date(v).getTime()
            return isNaN(t) ? null : t
        }

        const breaks = (tc.lines || [])
            .filter((l: any) => l.mserp_timeandattendancejobid === ENV_JID_BREAK && !l.delete)
            .map((b: any) => ({ amountSeconds: b.mserp_calculatedtimeseconds ?? 0 }))

        const lines = (tc.lines || [])
            .filter((l: any) => l.mserp_timeandattendancejobid !== ENV_JID_BREAK && l.mserp_timeandattendancejobid !== ENV_JID_START && l.mserp_timeandattendancejobid !== ENV_JID_STOP && !l.delete)
            .map((l: any) => ({ project: l.mserp_projectid ?? '', activity: l.mserp_timeandattendancejobid ?? '', amountSeconds: l.mserp_calculatedtimeseconds ?? 0 }))

        return {
            header,
            startTimeMs: toMs(start?.mserp_startdatetime) ?? null,
            stopTimeMs: toMs(stop?.mserp_stopdatetime) ?? null,
            breaks,
            lines,
        }
    }

    const checkUnsaved = (): boolean => {
        if (!selectedDate || Object.keys(selectedDate).length === 0) return false

        if (baseline === null){
            resetBaseline()
            return false
        }

        const current = JSON.stringify(reduceForCompare(selectedDate))
        const changed = current !== baseline
        return changed
    }

     const resetBaseline = (tc?: ITimecard) => {
        // if a timecard is provided, use that otherwise state
        const target = tc ?? selectedDate
        if (target) {
            const value = JSON.stringify(reduceForCompare(target))
            dispatch(setBaseline(value))
        }
    }

    return { checkUnsaved, resetBaseline }
}

const ChangedDataWarning = ({ open: controlledOpen, onConfirm, onCancel }: { open?: boolean; onConfirm?: () => void; onCancel?: () => void }) => {
    const visible = controlledOpen === true

    const handleClose = (confirm: boolean) => () => {
        if (confirm) {
            if (onConfirm) onConfirm()
        } else {
            if (onCancel) onCancel()
        }
    }

    return (
        <Dialog open={visible} onClose={handleClose(false)} aria-labelledby="changed-data-warning-title">
            <DialogTitle id="changed-data-warning-title">{t('changedDataWarning.title')}</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {t('changedDataWarning.description')}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose(false)}>{t('changedDataWarning.cancel')}</Button>
                <Button onClick={handleClose(true)} autoFocus color="error">
                    {t('changedDataWarning.confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default ChangedDataWarning
